// MessageChannelPicker — "Send via" toggles shared by every coach composer
// (game-page Message Players, dashboard Message Players).
//
// 2026-09-17 (per Gilles, Phase 4):
//   • 📋 Player preferences — the DEFAULT. Each recipient gets every channel
//     they haven't opted out of in their own notification settings. It is
//     exclusive: turning it on clears the explicit picks; picking any
//     explicit channel turns it off.
//   • 📧 Email / 📱 Push / 💬 SMS — explicit picks, any combination, each
//     with a ✓ when selected. "In-app" is gone — Push already writes the
//     in-app notification and reaches every registered device, including
//     an open desktop tab.
//   • SMS is greyed out (disabled, with an explanation) unless the team's
//     plan has SMS switched on (`smsEnabled`).
//
// Props: value (string[]), onChange(next: string[]), smsEnabled (bool),
// disabled (bool). `value` uses the backend channel keys:
// 'prefs' | 'email' | 'push' | 'sms'.
export const DEFAULT_CHANNELS = ['prefs'];

export default function MessageChannelPicker({ value, onChange, smsEnabled = false, disabled = false }) {
  const channels = Array.isArray(value) && value.length ? value : DEFAULT_CHANNELS;
  const isOn = (k) => channels.includes(k);

  const toggle = (key) => {
    if (disabled) return;
    if (key === 'prefs') {
      // Exclusive — and it can't be turned off into nothing: tapping it
      // while it's the only channel just leaves it on.
      onChange(['prefs']);
      return;
    }
    if (key === 'sms' && !smsEnabled) return;
    let next = channels.filter(c => c !== 'prefs');
    next = isOn(key) ? next.filter(c => c !== key) : [...next, key];
    if (next.length === 0) next = ['prefs']; // never zero channels
    onChange(next);
  };

  const opts = [
    { key: 'prefs', emoji: '📋', label: 'Player preferences',
      title: 'Each player gets the channels they chose in their own notification settings (email, push and/or SMS). Recommended.' },
    { key: 'email', emoji: '📧', label: 'Email', title: 'Send an email to everyone who hasn\'t turned email off.' },
    { key: 'push',  emoji: '📱', label: 'Push',
      title: 'Push notification to every device the player has registered — phone app and an open desktop tab — plus their in-app inbox.' },
    { key: 'sms',   emoji: '💬', label: 'SMS',
      title: smsEnabled
        ? 'Text message to players with a verified number and an active Messaging subscription.'
        : 'SMS isn\'t enabled for this team\'s plan — turn it on under Team Settings → SMS.' },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-1">
        {opts.map(opt => {
          const active = isOn(opt.key);
          const off = disabled || (opt.key === 'sms' && !smsEnabled);
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => toggle(opt.key)}
              disabled={off}
              title={opt.title}
              aria-pressed={active}
              className="flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg text-xs font-bold transition-all"
              style={{
                background: active ? 'var(--ice-a8)' : 'var(--surface-2)',
                border: `1.5px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
                color: active ? 'var(--ice)' : 'var(--text-2)',
                cursor: off ? 'not-allowed' : 'pointer',
                opacity: off ? 0.45 : 1,
                textDecoration: (opt.key === 'sms' && !smsEnabled) ? 'line-through' : 'none',
              }}>
              {active ? '✓ ' : ''}{opt.emoji} {opt.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-3)', lineHeight: 1.4 }}>
        {isOn('prefs')
          ? 'Each player gets the channels they picked in their own settings. Tap Email, Push or SMS to override.'
          : `Sending via ${channels.map(c => c === 'sms' ? 'SMS' : c[0].toUpperCase() + c.slice(1)).join(' + ')}${!smsEnabled ? ' · SMS unavailable on this plan' : ''}.`}
      </p>
    </div>
  );
}
